import { useRef, useState } from "react";
import { notification } from "antd";
import { useDispatch } from "react-redux";
import { getAllHighRisksData, getAllSwmsDropdowns, getSwmsFormData } from "../../../../../../../actions/v2/JobSwmsActions";
import { updateJobSwmsPreStartChecklist } from "../../../../../../../actions/v2/JobSwmsPreStartChecklistActions";
import { getJobSwmsEmergencyPlans } from "../../../../../../../actions/v2/JobSwmsEmergencyPlansActions";
import { getMethodItemsAdditionals } from "../../../../../../../actions/v2/SwmsMethodActions";
import { getJobSwmsEmergencyProcedures } from "../../../../../../../actions/v2/JobSwmsEmergencyProceduresActions";
import { getJobSwmsEmergencyReadiness } from "../../../../../../../actions/v2/JobSwmsEmergencyReadinessActions";
import { Strings } from "../../../../../../../dataProvider/localize";
import { normalizeChecklistPayload, reorderSwmsDataSections } from "./utils/preStartChecklistV3.utils";
import { buildPreStartEditItems, buildPreStartToggleEditItems, createSwmsEditContext } from "../editHistory/swmsEditContext";

const getChecklistQuestions = (checklist = {}) => {
  return checklist
    && checklist.data
    && Array.isArray(checklist.data.pre_start_questions)
    ? checklist.data.pre_start_questions
    : [];
};

const createPreStartSaveMetadata = (formData, baseline, submitted, items = buildPreStartEditItems(baseline, submitted)) => {
  const swmsData = formData && formData.swms_data ? formData.swms_data : {};
  return {
    version: swmsData.currentVersion,
    edit_context: createSwmsEditContext({
      initialValues: baseline,
      submittedValues: submitted,
      internalRevisionId: Number.isInteger(Number(swmsData.internal_revision_id))
        ? Number(swmsData.internal_revision_id)
        : 0,
      documentName: "Pre-Start",
      documentType: "pre_start",
      items
    })
  };
};

export default function usePreStartChecklistApi({
  onClose,
  resolvedJobId,
  resolvedSwmsId,
  activeTab,
  tabPreStart,
  preStartToggle,
  onPreStartToggleChange
}) {
  const dispatch = useDispatch();
  const controller = useRef();

  const [formData, setFormData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [methodItemTypeGroups, setMethodItemTypeGroups] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [refreshingAfterSave, setRefreshingAfterSave] = useState(false);
  const [savingPreStartChecklist, setSavingPreStartChecklist] = useState(false);
  const [updatingPreStartToggle, setUpdatingPreStartToggle] = useState(false);
  const [pendingPreStartToggle, setPendingPreStartToggle] = useState(null);
  const [toggleConfirmVisible, setToggleConfirmVisible] = useState(false);

  const checklistRequestInFlight = updatingPreStartToggle || savingPreStartChecklist;

  const abortRequests = () => {
    if (controller.current) {
      controller.current.abort();
    }
  };

  const closeToggleConfirmModal = () => {
    if (updatingPreStartToggle) {
      return;
    }

    setToggleConfirmVisible(false);
    setPendingPreStartToggle(null);
  };

  const loadFormData = ({ closeOnError = true, showBlockingLoader = true, preservePreStartChecklist = null, onError = null } = {}) => {
    if (showBlockingLoader) {
      setLoadingData(true);
    } else {
      setRefreshingAfterSave(true);
    }

    controller.current = new AbortController();

    const requestEmergencyData = (request) => request.catch((error) => (
      error ? { error } : null
    ));
    const requests = [
      dispatch(getAllSwmsDropdowns({ signal: controller.current.signal })),
      dispatch(getSwmsFormData({ signal: controller.current.signal }, resolvedSwmsId, resolvedJobId)),
      dispatch(getAllHighRisksData({ signal: controller.current.signal })),
      requestEmergencyData(dispatch(getJobSwmsEmergencyPlans({ signal: controller.current.signal }, resolvedSwmsId, resolvedJobId))),
      requestEmergencyData(dispatch(getMethodItemsAdditionals({ signal: controller.current.signal }))),
      requestEmergencyData(dispatch(getJobSwmsEmergencyProcedures({ signal: controller.current.signal }, resolvedSwmsId, resolvedJobId))),
      requestEmergencyData(dispatch(getJobSwmsEmergencyReadiness({ signal: controller.current.signal }, resolvedSwmsId, resolvedJobId)))
    ];

    Promise.all(requests)
      .then(([
        ,
        apiFormData,
        ,
        emergencyPlans,
        methodItemTypeGroupsResponse,
        emergencyProcedures,
        emergencyReadiness
      ]) => {
        const normalizedSwmsData = reorderSwmsDataSections(apiFormData && apiFormData.swms_data ? apiFormData.swms_data : null);
        const resolvedSwmsData = {
          ...(normalizedSwmsData || {}),
          ...(preservePreStartChecklist
            ? { pre_start_checklist: preservePreStartChecklist }
            : {}),
          emergency_plans: emergencyPlans || {},
          emergency_procedures: emergencyProcedures || {},
          emergency_readiness: emergencyReadiness || {}
        };

        setFormData(apiFormData ? {
          ...apiFormData,
          swms_data: resolvedSwmsData
        } : null);
        setCategories(apiFormData && Array.isArray(apiFormData.categories) ? apiFormData.categories : []);
        setMethodItemTypeGroups(Array.isArray(methodItemTypeGroupsResponse) ? methodItemTypeGroupsResponse : []);
        if (showBlockingLoader) {
          setLoadingData(false);
        }
        setRefreshingAfterSave(false);
      })
      .catch((err) => {
        if (showBlockingLoader) {
          setLoadingData(false);
        }
        setRefreshingAfterSave(false);
        if (onError) {
          onError(err || Strings.generic_error);
          return;
        }

        if (err) {
          notification.error({
            message: Strings.error_title,
            description: err || Strings.generic_error,
            className: "ant-error"
          });
        }

        if (closeOnError) {
          onClose();
        }
      });
  };

  const handleSaveQuestions = ({
    savedPreStartChecklist,
    workingPreStartChecklist,
    hasUnsavedQuestionChanges,
    replaceDraftWithServerChecklist
  }) => {
    if (checklistRequestInFlight || !hasUnsavedQuestionChanges) {
      return;
    }

    const savedBaselineChecklist = normalizeChecklistPayload(savedPreStartChecklist);
    const workingChecklist = normalizeChecklistPayload(workingPreStartChecklist);
    const requestedChecklist = {
      ...savedBaselineChecklist,
      ...workingChecklist,
      data: {
        ...savedBaselineChecklist.data,
        ...workingChecklist.data,
        pre_start_questions: getChecklistQuestions(workingChecklist)
      }
    };

    setSavingPreStartChecklist(true);
    dispatch(updateJobSwmsPreStartChecklist(
      resolvedJobId,
      resolvedSwmsId,
      requestedChecklist,
      createPreStartSaveMetadata(formData, savedBaselineChecklist, requestedChecklist)
    )).then((response = {}) => {
      const responseChecklist = replaceDraftWithServerChecklist(response.pre_start_checklist || requestedChecklist);
      onPreStartToggleChange(responseChecklist.toggle === 1);

      notification.success({
        message: Strings.success_title,
        description: response.message || "Pre-Start checklist updated successfully.",
        className: "ant-success"
      });

      loadFormData({
        closeOnError: false,
        showBlockingLoader: false,
        preservePreStartChecklist: responseChecklist,
        onError: (refreshError) => {
          notification.error({
            message: Strings.error_title,
            description: refreshError || Strings.generic_error,
            className: "ant-error"
          });
        }
      });
    }).catch((err) => {
      notification.error({
        message: Strings.error_title,
        description: err || Strings.generic_error,
        className: "ant-error"
      });
    }).finally(() => {
      setSavingPreStartChecklist(false);
    });
  };

  const openToggleConfirmModal = (checked) => {
    if (activeTab !== tabPreStart || checklistRequestInFlight) {
      return;
    }

    const requestedToggle = Boolean(checked);
    if (requestedToggle === preStartToggle) {
      return;
    }

    setPendingPreStartToggle(requestedToggle);
    setToggleConfirmVisible(true);
  };

  const handleConfirmPreStartToggle = ({
    savedPreStartChecklist,
    applyToggleServerChecklist
  }) => {
    if (pendingPreStartToggle === null || checklistRequestInFlight) {
      return;
    }

    const savedBaselineChecklist = normalizeChecklistPayload(savedPreStartChecklist);
    const requestedChecklist = {
      ...savedBaselineChecklist,
      toggle: pendingPreStartToggle ? 1 : 0
    };

    setUpdatingPreStartToggle(true);
    dispatch(updateJobSwmsPreStartChecklist(
      resolvedJobId,
      resolvedSwmsId,
      requestedChecklist,
      createPreStartSaveMetadata(
        formData,
        savedBaselineChecklist,
        requestedChecklist,
        buildPreStartToggleEditItems(savedBaselineChecklist, requestedChecklist)
      )
    )).then((response = {}) => {
      const responseChecklist = applyToggleServerChecklist(response.pre_start_checklist || requestedChecklist);

      onPreStartToggleChange(responseChecklist.toggle === 1);
      setToggleConfirmVisible(false);
      setPendingPreStartToggle(null);

      notification.success({
        message: Strings.success_title,
        description: response.message || "Pre-Start checklist updated successfully.",
        className: "ant-success"
      });

      return loadFormData({
        closeOnError: false,
        showBlockingLoader: false,
        preservePreStartChecklist: responseChecklist,
        onError: (refreshError) => {
          notification.error({
            message: Strings.error_title,
            description: refreshError || Strings.generic_error,
            className: "ant-error"
          });
        }
      });
    }).catch((err) => {
      onPreStartToggleChange(savedBaselineChecklist.toggle === 1);
      setToggleConfirmVisible(false);
      setPendingPreStartToggle(null);

      notification.error({
        message: Strings.error_title,
        description: err || Strings.generic_error,
        className: "ant-error"
      });
    }).finally(() => {
      setUpdatingPreStartToggle(false);
    });
  };

  return {
    formData,
    categories,
    methodItemTypeGroups: Array.isArray(methodItemTypeGroups) ? methodItemTypeGroups : [],
    loadingData,
    refreshingAfterSave,
    checklistRequestInFlight,
    savingPreStartChecklist,
    updatingPreStartToggle,
    pendingPreStartToggle,
    toggleConfirmVisible,
    loadFormData,
    abortRequests,
    closeToggleConfirmModal,
    openToggleConfirmModal,
    handleSaveQuestions,
    handleConfirmPreStartToggle
  };
}
